import { useEffect, useState } from "react";
import {
  lerParametros,
  salvarEscalaresDaFolha,
  salvarParametrosMei,
  salvarSalarioMinimo,
  salvarTabela,
  type ParametrosDoAdmin,
} from "../lib/parametrosAdmin";
import type { FaixaProgressiva } from "../lib/folhaDp";
import { reais } from "../lib/folhaDp";
import { CAMPO, Recado } from "./ui";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";

/**
 * Admin → Parâmetros: os números que expiram em janeiro.
 *
 * ## Por que esta tela existe
 *
 * `dp_parametros`/`dp_faixas` (HUB DP, 000108) e `mei_parametros` (Simulador MEI,
 * 000098) nasceram com as RPCs de escrita prontas e provadas, **e sem tela**. A
 * consequência era a mesma nas duas: no primeiro dia útil de janeiro, alguém tem
 * de rodar SQL à mão contra produção para o salário mínimo novo entrar — e até
 * fazer isso, as duas ferramentas ensinam o valor do ano passado a 37 mil e 35 mil
 * alunos.
 *
 * Victor pediu a tela em 25/08, depois de eu ter listado a ausência dela como
 * pendência do porte do HUB DP.
 *
 * ## Por que uma aba só para as duas tabelas
 *
 * Porque o trabalho é UM: virou o ano, os números mudam. Separar em duas abas
 * faria a equipe visitar dois lugares para o mesmo ato — e é assim que se esquece
 * o segundo. Ainda mais com o salário mínimo, que hoje mora nas duas (ver o
 * cabeçalho de `parametrosAdmin.ts`).
 *
 * ## O que esta tela NÃO decide
 *
 * **Nada.** Quem pode escrever é `pode_editar('parametros')` (000297), DENTRO de cada RPC; quem valida o
 * valor são as próprias RPCs (recusam zero, exigem tabela progressiva coerente,
 * conferem se a alíquota é fração). A tela não esconde botão para se proteger e
 * não repete nenhuma validação — reescrevê-las aqui criaria a segunda definição
 * delas, e a segunda é a que fica desatualizada.
 *
 * O que ela faz é MOSTRAR a recusa do banco com as palavras do banco: as
 * mensagens são informativas de propósito ("a alíquota da faixa 2 é menor que a
 * anterior"), e quem está do outro lado é quem mantém a tabela.
 *
 * ## O que ela NÃO cobre, e é decisão
 *
 * A **regra** de a redução do IR de 2026 incidir sobre férias (a constante
 * `APLICAR_REDUCAO_IR` em `feriasDecimoDp.ts`) não entra aqui. Não é um
 * parâmetro: é comportamento do cálculo. O corte está no CLAUDE.md — número que
 * muda por ato do Executivo vai para o banco; regra de como o cálculo se comporta
 * fica no código. Uma chave de tela que altera imposto retido de gente real, e
 * que ninguém vai tocar depois de decidida uma vez, é campo que só serve para
 * alguém errar. Victor vai confirmar essa decisão com a equipe de produtos.
 */
export default function AdminParametros() {
  const [dados, setDados] = useState<ParametrosDoAdmin | null>(null);
  const [erro, setErro] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const recarregar = async () => {
    const r = await lerParametros();
    if (!r.folha && !r.mei) {
      setErro(true);
      return;
    }
    setErro(false);
    setDados(r);
  };

  useEffect(() => {
    void recarregar();
  }, []);

  const aviso = (tipo: "ok" | "erro", texto: string) => {
    setRecado({ tipo, texto });
    // O recado some sozinho, mas só o de acerto: erro fica na tela até a próxima
    // ação, porque quem errou precisa poder LER a recusa do banco com calma.
    if (tipo === "ok") window.setTimeout(() => setRecado(null), 4000);
  };

  if (erro) {
    return (
      <Recado
        tipo="erro"
        texto="Não foi possível ler os parâmetros. Recarregue a página; se continuar, o banco pode estar fora do ar."
      />
    );
  }
  if (!dados) return <p className="text-sm text-cinza">Carregando os parâmetros…</p>;

  return (
    <div className="space-y-8">
      <header>
        <h2 className="font-display text-xl font-bold text-titulo">Parâmetros que expiram</h2>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-cinza">
          Os números que mudam por ato do Executivo ou lei anual, usados pelo HUB DP e pelo
          Simulador MEI. Toda alteração vale <strong>na hora</strong> para os alunos — não há
          publicação nem cache. O que a lei fixa (1/3 de férias, 8% do FGTS, os R$ 1,00 de ICMS do
          MEI) não está aqui: mora no código, porque só muda com reforma.
        </p>
      </header>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}

      {dados.salarioMinimoDivergente && (
        <div
          id="aviso-divergencia"
          className="rounded-cf border-l-4 border-erro-tinta bg-erro-fundo px-4 py-3 text-sm leading-relaxed text-erro-tinta"
        >
          <strong className="block">As duas tabelas discordam do salário mínimo.</strong>
          O HUB DP está usando {reais(dados.folha?.salarioMinimo ?? 0)} e o Simulador MEI{" "}
          {reais(dados.mei?.salario_minimo ?? 0)}. Uma das duas ferramentas está calculando com o
          valor errado <em>agora</em>. Salve o campo abaixo para igualar as duas.
        </div>
      )}

      <SalarioMinimo dados={dados} aoSalvar={recarregar} aviso={aviso} />

      {dados.folha && <Folha folha={dados.folha} aoSalvar={recarregar} aviso={aviso} />}
      {dados.mei && <Mei mei={dados.mei} aoSalvar={recarregar} aviso={aviso} />}

      <p className="border-t border-borda pt-4 text-xs leading-relaxed text-cinza">
        <strong className="text-tinta">A tarefa de janeiro.</strong> Salário mínimo novo, tabela do
        INSS nova, tabela do IRRF nova. As três entram por esta tela, e o efeito é imediato nas duas
        ferramentas. Confira depois em{" "}
        <span className="font-semibold">/ferramenta/hub-dp/holerite/referencia</span>, que mostra as
        tabelas como o aluno as vê.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O salário mínimo, que mora nas duas tabelas
// ---------------------------------------------------------------------------

type Aviso = (tipo: "ok" | "erro", texto: string) => void;

/**
 * Um campo, duas gravações.
 *
 * Fica em seção própria e no topo porque é o número que a equipe vem mudar em
 * janeiro, e porque é o único que grava em dois lugares — merece a explicação ao
 * lado, não uma nota de pé.
 */
function SalarioMinimo({
  dados,
  aoSalvar,
  aviso,
}: {
  dados: ParametrosDoAdmin;
  aoSalvar: () => Promise<void>;
  aviso: Aviso;
}) {
  const atual = dados.folha?.salarioMinimo ?? dados.mei?.salario_minimo ?? 0;
  const [texto, setTexto] = useState(moedaDeNumero(atual));
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setTexto(moedaDeNumero(atual));
  }, [atual]);

  const valor = mascararMoeda(texto).numero;
  const mudou = valor !== null && valor !== atual;

  return (
    <section id="secao-salario-minimo" className="surface p-5">
      <h3 className="text-sm font-bold text-titulo">Salário mínimo</h3>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-cinza">
        Base da insalubridade no HUB DP e do INSS do MEI. Hoje ele está guardado em{" "}
        <strong>duas tabelas</strong> (`dp_parametros` e `mei_parametros`) — este campo grava nas
        duas de uma vez. A dívida de haver duas cópias está registrada no runbook; até ela ser paga,
        salve sempre por aqui, nunca por SQL num lado só.
      </p>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-cinza">Valor vigente</span>
          <input
            className={`${CAMPO} w-44`}
            inputMode="numeric"
            value={texto}
            onChange={(e) => setTexto(mascararMoeda(e.target.value).texto)}
          />
        </label>
        <button
          type="button"
          className="pill-cta"
          disabled={!mudou || salvando}
          onClick={async () => {
            if (valor === null) return;
            setSalvando(true);
            const erro = await salvarSalarioMinimo(valor);
            setSalvando(false);
            if (erro) aviso("erro", erro);
            else aviso("ok", `Salário mínimo alterado para ${reais(valor)} nas duas tabelas.`);
            await aoSalvar();
          }}
        >
          {salvando ? "Salvando…" : "Salvar nas duas"}
        </button>
        {mudou && valor !== null && (
          <p className="text-xs leading-relaxed text-alerta-tinta">
            De {reais(atual)} para {reais(valor)}. O INSS do MEI passa a ser{" "}
            {reais(Math.round(valor * (dados.mei?.aliquota_inss_geral ?? 0.05) * 100) / 100)}.
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// A folha (HUB DP)
// ---------------------------------------------------------------------------

function Folha({
  folha,
  aoSalvar,
  aviso,
}: {
  folha: import("../lib/folhaDp").ParametrosFolha;
  aoSalvar: () => Promise<void>;
  aviso: Aviso;
}) {
  return (
    <section className="space-y-5">
      <div>
        <h3 className="font-display text-lg font-bold text-titulo">Folha de pagamento — HUB DP</h3>
        <p className="text-xs text-cinza">
          Usado por Holerite, Férias e 13º, Encargos e eSocial. 37.056 alunos.
        </p>
      </div>

      <Escalares folha={folha} aoSalvar={aoSalvar} aviso={aviso} />
      <Tabela
        tributo="inss"
        titulo="Tabela do INSS"
        nota="Progressiva por parcela a deduzir. O teto e o desconto máximo são DERIVADOS da última faixa — não há campo para eles, e é de propósito."
        faixas={folha.faixas.inss}
        aoSalvar={aoSalvar}
        aviso={aviso}
      />
      <Tabela
        tributo="irrf"
        titulo="Tabela do IRRF"
        nota="A última faixa fica SEM teto (deixe o limite em branco) — é ela que cobre qualquer rendimento acima da penúltima."
        faixas={folha.faixas.irrf}
        aoSalvar={aoSalvar}
        aviso={aviso}
      />
    </section>
  );
}

function Escalares({
  folha,
  aoSalvar,
  aviso,
}: {
  folha: import("../lib/folhaDp").ParametrosFolha;
  aoSalvar: () => Promise<void>;
  aviso: Aviso;
}) {
  const [dependente, setDependente] = useState(moedaDeNumero(folha.deducaoDependente));
  const [simplificado, setSimplificado] = useState(moedaDeNumero(folha.descontoSimplificado));
  const [refInss, setRefInss] = useState(folha.referenciaInss);
  const [refIrrf, setRefIrrf] = useState(folha.referenciaIrrf);
  const [limInt, setLimInt] = useState(moedaDeNumero(folha.reducaoIr.limiteIntegral));
  const [valInt, setValInt] = useState(moedaDeNumero(folha.reducaoIr.valorIntegral));
  const [limFim, setLimFim] = useState(moedaDeNumero(folha.reducaoIr.limiteFinal));
  const [base, setBase] = useState(moedaDeNumero(folha.reducaoIr.base));
  // Com VÍRGULA: toda a tela usa o separador brasileiro, e o coeficiente
  // aparecendo como "0.133145" no meio de "R$ 5.000,00" faz a tela parecer
  // descuidada — e sugere que aquele campo espera outra convenção. A leitura
  // aceita os dois (`replace(",", ".")`), então quem colar de uma tabela oficial
  // com ponto continua funcionando.
  const [coef, setCoef] = useState(String(folha.reducaoIr.coeficiente).replace(".", ","));
  const [salvando, setSalvando] = useState(false);

  const n = (t: string) => mascararMoeda(t).numero ?? 0;
  // A conferência que a tela FAZ, e é a única: a reta da redução tem de encostar
  // no valor fixo na fronteira, senão o imposto salta de um centavo de renda para
  // o outro. Não é validação duplicada — o banco prova isso na 000108, mas ali é
  // tarde: aqui o aviso aparece ANTES de salvar, enquanto a equipe digita.
  const retaNaFronteira = Math.round((n(base) - Number(coef.replace(",", ".")) * n(limInt)) * 100) / 100;
  const salta = Math.abs(retaNaFronteira - n(valInt)) > 0.01;

  return (
    <div className="surface space-y-4 p-5">
      <h4 className="text-xs font-bold tracking-[0.12em] text-titulo uppercase">
        Deduções e referências
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Dedução por dependente" ajuda="IRRF, só no Modelo 1 (completo).">
          <input className={CAMPO} inputMode="numeric" value={dependente} onChange={(e) => setDependente(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo rotulo="Desconto simplificado" ajuda="IRRF, Modelo 2. Substitui todas as deduções legais, o INSS incluído.">
          <input className={CAMPO} inputMode="numeric" value={simplificado} onChange={(e) => setSimplificado(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo
          rotulo="Referência do INSS"
          ajuda="O texto que o ALUNO lê no pé de cada módulo. É o que impede a ferramenta de ensinar valor velho como atual — escreva de que ano é a tabela."
        >
          <input className={CAMPO} value={refInss} onChange={(e) => setRefInss(e.target.value)} />
        </Campo>
        <Campo rotulo="Referência do IRRF" ajuda="Idem. Hoje diz que a tabela é de maio/2025.">
          <input className={CAMPO} value={refIrrf} onChange={(e) => setRefIrrf(e.target.value)} />
        </Campo>
      </div>

      <h4 className="border-t border-borda pt-4 text-xs font-bold tracking-[0.12em] text-titulo uppercase">
        Redução mensal do IRPF
      </h4>
      <p className="text-xs leading-relaxed text-cinza">
        Duas faixas: até o primeiro limite abate-se o valor fixo; daí até o limite final, abate-se{" "}
        <span className="font-semibold">base − coeficiente × rendimento</span>. Os cinco andam
        juntos.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo rotulo="Limite da faixa integral">
          <input className={CAMPO} inputMode="numeric" value={limInt} onChange={(e) => setLimInt(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo rotulo="Valor da faixa integral">
          <input className={CAMPO} inputMode="numeric" value={valInt} onChange={(e) => setValInt(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo rotulo="Limite final (zera aqui)">
          <input className={CAMPO} inputMode="numeric" value={limFim} onChange={(e) => setLimFim(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo rotulo="Base da reta">
          <input className={CAMPO} inputMode="numeric" value={base} onChange={(e) => setBase(mascararMoeda(e.target.value).texto)} />
        </Campo>
        <Campo rotulo="Coeficiente" ajuda="Use ponto ou vírgula. Ex.: 0,133145">
          <input className={CAMPO} inputMode="decimal" value={coef} onChange={(e) => setCoef(e.target.value)} />
        </Campo>
      </div>

      {salta && (
        <p id="aviso-salto" className="rounded-cf bg-alerta-fundo px-3 py-2 text-xs leading-relaxed text-alerta-tinta">
          <strong>A reta não encosta no valor fixo.</strong> No limite de {reais(n(limInt))} a
          fórmula dá {reais(retaNaFronteira)}, e o valor fixo é {reais(n(valInt))}. Com essa
          diferença o imposto salta de um centavo de renda para o outro — confira os cinco números
          antes de salvar.
        </p>
      )}

      <button
        type="button"
        className="pill-cta"
        disabled={salvando}
        onClick={async () => {
          setSalvando(true);
          const erro = await salvarEscalaresDaFolha({
            deducaoDependente: n(dependente),
            descontoSimplificado: n(simplificado),
            reducaoLimiteIntegral: n(limInt),
            reducaoValorIntegral: n(valInt),
            reducaoLimiteFinal: n(limFim),
            reducaoBase: n(base),
            reducaoCoeficiente: Number(coef.replace(",", ".")) || 0,
            referenciaInss: refInss,
            referenciaIrrf: refIrrf,
          });
          setSalvando(false);
          if (erro) aviso("erro", erro);
          else aviso("ok", "Deduções e referências salvas.");
          await aoSalvar();
        }}
      >
        {salvando ? "Salvando…" : "Salvar deduções e referências"}
      </button>
    </div>
  );
}

/**
 * Uma tabela progressiva, editada INTEIRA.
 *
 * Não há "salvar uma faixa": `dp_definir_faixas` substitui o conjunto, e o motivo
 * está na 000108 — o estado intermediário (limite novo, parcela velha) daria
 * número errado sem erro na tela, e todo aluno que abrisse a calculadora naquele
 * intervalo receberia um holerite incorreto.
 */
function Tabela({
  tributo,
  titulo,
  nota,
  faixas,
  aoSalvar,
  aviso,
}: {
  tributo: "inss" | "irrf";
  titulo: string;
  nota: string;
  faixas: FaixaProgressiva[];
  aoSalvar: () => Promise<void>;
  aviso: Aviso;
}) {
  type Linha = { limite: string; aliquota: string; parcela: string };
  const paraLinha = (f: FaixaProgressiva): Linha => ({
    limite: f.limite === null ? "" : moedaDeNumero(f.limite),
    // Alíquota mostrada em PERCENTUAL (7,5) e gravada em fração (0,075). A
    // conversão é aqui, na borda: pedir fração ao operador é convite a digitar
    // 7,5 e criar um INSS dez vezes maior — e a RPC recusaria, mas depois de o
    // erro ter sido cometido.
    aliquota: String(Math.round(f.aliquota * 100000) / 1000).replace(".", ","),
    parcela: moedaDeNumero(f.parcelaDeduzir),
  });

  const [linhas, setLinhas] = useState<Linha[]>(faixas.map(paraLinha));
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setLinhas(faixas.map(paraLinha));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faixas]);

  const trocar = (i: number, campo: keyof Linha, valor: string) =>
    setLinhas((atual) => atual.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));

  return (
    <div className="surface p-5" data-tabela={tributo}>
      <h4 className="text-xs font-bold tracking-[0.12em] text-titulo uppercase">{titulo}</h4>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-cinza">{nota}</p>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="bg-superficie-2">
              {["#", "Limite (em branco = sem teto)", "Alíquota %", "Parcela a deduzir", ""].map((c) => (
                <th key={c} scope="col" className="px-2 py-2 text-left text-[11px] font-semibold text-cinza uppercase">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i}>
                <td className="border-t border-borda px-2 py-2 text-xs text-cinza tabular-nums">{i + 1}</td>
                <td className="border-t border-borda px-2 py-2">
                  <input
                    className={`${CAMPO} w-36`}
                    inputMode="numeric"
                    aria-label={`Limite da faixa ${i + 1} do ${tributo}`}
                    value={l.limite}
                    onChange={(e) => trocar(i, "limite", mascararMoeda(e.target.value).texto)}
                  />
                </td>
                <td className="border-t border-borda px-2 py-2">
                  <input
                    className={`${CAMPO} w-24`}
                    inputMode="decimal"
                    aria-label={`Alíquota da faixa ${i + 1} do ${tributo}`}
                    value={l.aliquota}
                    onChange={(e) => trocar(i, "aliquota", e.target.value)}
                  />
                </td>
                <td className="border-t border-borda px-2 py-2">
                  <input
                    className={`${CAMPO} w-36`}
                    inputMode="numeric"
                    aria-label={`Parcela a deduzir da faixa ${i + 1} do ${tributo}`}
                    value={l.parcela}
                    onChange={(e) => trocar(i, "parcela", mascararMoeda(e.target.value).texto)}
                  />
                </td>
                <td className="border-t border-borda px-2 py-2">
                  <button
                    type="button"
                    className="text-xs font-semibold text-erro-tinta hover:underline"
                    onClick={() => setLinhas((a) => a.filter((_, j) => j !== i))}
                  >
                    remover
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-contorno text-xs"
          onClick={() => setLinhas((a) => [...a, { limite: "", aliquota: "", parcela: "" }])}
        >
          + Faixa
        </button>
        <button
          type="button"
          className="btn-contorno text-xs"
          onClick={() => setLinhas(faixas.map(paraLinha))}
        >
          Desfazer as alterações
        </button>
        <button
          type="button"
          className="pill-cta text-sm"
          disabled={salvando}
          onClick={async () => {
            setSalvando(true);
            const erro = await salvarTabela(
              tributo,
              linhas.map((l) => ({
                limite: l.limite.trim() === "" ? null : (mascararMoeda(l.limite).numero ?? 0),
                aliquota: (Number(l.aliquota.replace(",", ".")) || 0) / 100,
                parcelaDeduzir: mascararMoeda(l.parcela).numero ?? 0,
              })),
            );
            setSalvando(false);
            if (erro) aviso("erro", `${titulo}: ${erro}`);
            else aviso("ok", `${titulo} substituída — ${linhas.length} faixa(s).`);
            await aoSalvar();
          }}
        >
          {salvando ? "Salvando…" : `Substituir a ${titulo.toLowerCase()}`}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O MEI (Simulador MEI)
// ---------------------------------------------------------------------------

function Mei({
  mei,
  aoSalvar,
  aviso,
}: {
  mei: import("../lib/dasMei").ParametrosDas;
  aoSalvar: () => Promise<void>;
  aviso: Aviso;
}) {
  const [teto, setTeto] = useState(moedaDeNumero(mei.teto));
  const [tetoCam, setTetoCam] = useState(moedaDeNumero(mei.teto_caminhoneiro));
  const [aliq, setAliq] = useState(String(Math.round(mei.aliquota_inss_geral * 10000) / 100).replace(".", ","));
  const [aliqCam, setAliqCam] = useState(String(Math.round(mei.aliquota_inss_caminhoneiro * 10000) / 100).replace(".", ","));
  const [icms, setIcms] = useState(moedaDeNumero(mei.icms));
  const [iss, setIss] = useState(moedaDeNumero(mei.iss));
  const [salvando, setSalvando] = useState(false);
  const n = (t: string) => mascararMoeda(t).numero ?? 0;
  const pct = (t: string) => (Number(t.replace(",", ".")) || 0) / 100;

  return (
    <section className="space-y-4">
      <div>
        <h3 className="font-display text-lg font-bold text-titulo">MEI — Simulador</h3>
        <p className="text-xs text-cinza">
          Usado pela apuração do DAS e pela DASN. 35.139 alunos. O INSS do DAS é{" "}
          <strong>derivado</strong> (salário mínimo × alíquota) — não há campo para ele.
        </p>
      </div>

      <div className="surface space-y-4 p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Teto anual de faturamento">
            <input className={CAMPO} inputMode="numeric" value={teto} onChange={(e) => setTeto(mascararMoeda(e.target.value).texto)} />
          </Campo>
          <Campo rotulo="Teto do caminhoneiro">
            <input className={CAMPO} inputMode="numeric" value={tetoCam} onChange={(e) => setTetoCam(mascararMoeda(e.target.value).texto)} />
          </Campo>
          <Campo rotulo="Alíquota do INSS — geral (%)" ajuda="5% do salário mínimo (LC 123/2006).">
            <input className={CAMPO} inputMode="decimal" value={aliq} onChange={(e) => setAliq(e.target.value)} />
          </Campo>
          <Campo rotulo="Alíquota do INSS — caminhoneiro (%)" ajuda="12% do salário mínimo (LC 188/2021).">
            <input className={CAMPO} inputMode="decimal" value={aliqCam} onChange={(e) => setAliqCam(e.target.value)} />
          </Campo>
          <Campo rotulo="ICMS do DAS" ajuda="Valor fixo da LC 123/2006, art. 18-A. Aceita zero, se a lei deixar de cobrar.">
            <input className={CAMPO} inputMode="numeric" value={icms} onChange={(e) => setIcms(mascararMoeda(e.target.value).texto)} />
          </Campo>
          <Campo rotulo="ISS do DAS" ajuda="Idem. Aceita zero.">
            <input className={CAMPO} inputMode="numeric" value={iss} onChange={(e) => setIss(mascararMoeda(e.target.value).texto)} />
          </Campo>
        </div>

        <p className="rounded-cf bg-chip px-3 py-2 text-xs leading-relaxed text-tinta">
          Com o salário mínimo de {reais(mei.salario_minimo)}, o INSS do DAS fica em{" "}
          <strong>{reais(Math.round(mei.salario_minimo * pct(aliq) * 100) / 100)}</strong> (geral) e{" "}
          <strong>{reais(Math.round(mei.salario_minimo * pct(aliqCam) * 100) / 100)}</strong>{" "}
          (caminhoneiro). Para mudar o salário mínimo, use a seção do topo.
        </p>

        <button
          type="button"
          className="pill-cta"
          disabled={salvando}
          onClick={async () => {
            setSalvando(true);
            const erro = await salvarParametrosMei({
              limite: n(teto),
              limiteCaminhoneiro: n(tetoCam),
              aliquotaInssGeral: pct(aliq),
              aliquotaInssCaminhoneiro: pct(aliqCam),
              icms: n(icms),
              iss: n(iss),
            });
            setSalvando(false);
            if (erro) aviso("erro", erro);
            else aviso("ok", "Parâmetros do MEI salvos.");
            await aoSalvar();
          }}
        >
          {salvando ? "Salvando…" : "Salvar os parâmetros do MEI"}
        </button>
      </div>
    </section>
  );
}

/** Rótulo + ajuda + campo. Igual ao das outras telas de admin. */
function Campo({
  rotulo,
  ajuda,
  children,
}: {
  rotulo: string;
  ajuda?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-cinza">{rotulo}</span>
      {children}
      {ajuda && <span className="mt-1 block text-[11px] leading-relaxed text-cinza">{ajuda}</span>}
    </label>
  );
}
